import { SeoHead } from "@/components/SeoHead";
import { getSeoCopy } from "@/i18n/seoCopy";
import { useI18n } from "@/i18n";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Button } from "@/components/ui/button";
import logo from "@/assets/aureum-logo-black-bg.jpg";

const gallery = [
  { src: "/images/products/luna-series-product-main.jpg", name: "LUNA" },
  { src: "/images/collection-2027/m86-monolith-hero.jpg", name: "REM7 M86 · Monolith" },
  { src: "/images/products/aria-series-product-main.jpg", name: "ARIA" },
  { src: "/images/products/k2-hero-desert.jpg", name: "REM7 K2" },
  { src: "/images/products/aurelius-series-product-main.jpg", name: "AURELIUS" },
  { src: "/images/products/luxa-series-product-side.jpg", name: "LUXA" },
];

const audiences = [
  { t: "Architects & interior designers", d: "Clean-lined smart toilets that sit quietly within considered bathroom schemes. Product information is shared on request for specification work." },
  { t: "Luxury hospitality", d: "A refined guest-bathroom experience for boutique hotels, resorts and serviced residences, discussed project by project." },
  { t: "Developers", d: "A single point of contact for multi-unit residential projects, from early selection through to delivery planning." },
  { t: "Showrooms & retailers", d: "A premium smart-toilet range to present to discerning clients, with trade terms agreed directly." },
];

const Page = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <section className={`brochure-page relative mx-auto my-8 flex w-full max-w-[210mm] flex-col overflow-hidden bg-background shadow-sm print:my-0 print:shadow-none ${className}`}>
    {children}
  </section>
);

const Folio = ({ n, dark = false }: { n: number; dark?: boolean }) => (
  <div className={`mt-auto flex items-center justify-between px-10 pb-8 text-[10px] uppercase tracking-[0.3em] ${dark ? "text-background/60" : "text-muted-foreground"}`}>
    <span>Aureum VII · Collection preview</span><span>0{n} / 05</span>
  </div>
);

export default function TradeBrochure() {
  const { language } = useI18n();
  const seo = getSeoCopy("tradeBrochure", language);
  return (
    <div className="min-h-screen bg-muted/40 print:bg-background">
      <SeoHead route="tradeBrochure" />
      <div className="sticky top-0 z-20 flex items-center justify-between border-b border-border/40 bg-background/95 px-4 py-3 backdrop-blur print:hidden sm:px-8">
        <LocalizedLink to="/business" className="text-sm text-muted-foreground hover:text-accent">← Trade &amp; Projects</LocalizedLink>
        <Button size="sm" onClick={() => window.print()} className="bg-accent text-accent-foreground hover:bg-accent/90">Print / Save as PDF</Button>
      </div>

      <main className="px-3 sm:px-6 print:p-0">
        {/* 1 Cover */}
        <Page className="bg-foreground text-background">
          <div className="flex flex-1 flex-col px-10 pt-14">
            <img src={logo} alt="Aureum VII Trading" className="h-28 w-auto self-start mix-blend-screen" />
            <div className="relative mt-10 flex-1 overflow-hidden">
              <img src="/images/products/luna-series-product-main.jpg" alt="LUNA smart toilet — collection preview" className="h-full max-h-[150mm] w-full object-cover" />
            </div>
            <p className="mt-10 text-[11px] uppercase tracking-[0.35em] text-accent">Collection preview · Trade edition</p>
            <h1 className="mt-4 font-playfair text-5xl font-light leading-tight sm:text-6xl">{seo.h1}</h1>
            <p className="mt-4 max-w-md text-background/70">REM7 &amp; the Aureum VII Collection — intelligent sanitary design for premium residences, hospitality and projects.</p>
          </div>
          <Folio n={1} dark />
        </Page>

        {/* 2 Positioning */}
        <Page>
          <div className="grid flex-1 grid-cols-1 gap-10 px-10 pt-16 sm:grid-cols-5">
            <div className="sm:col-span-3">
              <p className="text-[11px] uppercase tracking-[0.35em] text-accent">The brand</p>
              <h2 className="mt-4 font-playfair text-4xl leading-tight text-foreground">Quiet luxury for the modern bathroom</h2>
              <div className="mt-6 h-px w-16 bg-accent" />
              <p className="mt-6 leading-relaxed text-muted-foreground">Aureum VII Trading is a Marbella-based supplier of smart toilets and bathroom pieces for homeowners and trade enquiries. Each project is discussed individually.</p>
              <p className="mt-4 leading-relaxed text-muted-foreground">REM7 leads the collection preview, alongside the Aureum VII range. Product details and project terms are confirmed individually on request.</p>
              <ul className="mt-8 space-y-3 text-sm text-foreground">
                {["Design-led smart toilet collection", "Direct trade relationship", "Project-by-project support from Marbella", "Information shared on request for specification"].map((x) => (
                  <li key={x} className="flex gap-3"><span className="mt-2 h-1 w-4 shrink-0 bg-accent" />{x}</li>
                ))}
              </ul>
            </div>
            <div className="sm:col-span-2">
              <img src="/images/products/aria-series-lifestyle-luxury.jpg" alt="Aureum VII smart toilet in a bathroom setting" className="h-full max-h-[200mm] w-full object-cover" />
            </div>
          </div>
          <Folio n={2} />
        </Page>

        {/* 3 Gallery */}
        <Page>
          <div className="flex-1 px-10 pt-14">
            <p className="text-[11px] uppercase tracking-[0.35em] text-accent">Design gallery</p>
            <h2 className="mt-3 font-playfair text-4xl text-foreground">Form, finish &amp; presence</h2>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {gallery.map((g) => (
                <figure key={g.src} className="break-inside-avoid">
                  <img src={g.src} alt={`${g.name} smart toilet`} className="aspect-[4/3] w-full object-cover" />
                  <figcaption className="mt-2 text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{g.name}</figcaption>
                </figure>
              ))}
            </div>
          </div>
          <Folio n={3} />
        </Page>

        {/* 4 Partnership */}
        <Page className="bg-muted/50">
          <div className="flex-1 px-10 pt-16">
            <p className="text-[11px] uppercase tracking-[0.35em] text-accent">Partnership</p>
            <h2 className="mt-3 font-playfair text-4xl leading-tight text-foreground">Working with trade professionals</h2>
            <p className="mt-4 max-w-lg text-muted-foreground">Every project is discussed individually. We share the product information you need and agree terms directly.</p>
            <div className="mt-10 grid gap-px bg-border sm:grid-cols-2">
              {audiences.map((a, i) => (
                <div key={a.t} className="bg-background p-7">
                  <span className="font-playfair text-3xl text-accent">0{i + 1}</span>
                  <h3 className="mt-3 font-playfair text-xl text-foreground">{a.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.d}</p>
                </div>
              ))}
            </div>
            <img src="/images/products/nova-series-lifestyle.jpg" alt="Smart toilet in a residential bathroom" className="mt-8 h-[55mm] w-full object-cover" />
          </div>
          <Folio n={4} />
        </Page>

        {/* 5 CTA */}
        <Page className="bg-foreground text-background">
          <div className="flex flex-1 flex-col justify-center px-10 pt-16 text-center">
            <img src={logo} alt="" aria-hidden className="mx-auto h-24 w-auto mix-blend-screen" />
            <p className="mt-10 text-[11px] uppercase tracking-[0.35em] text-accent">Enquiries</p>
            <h2 className="mt-4 font-playfair text-4xl leading-tight sm:text-5xl">Discuss your project</h2>
            <p className="mx-auto mt-5 max-w-md text-background/70">Request trade information, book a consultation or join the post-arrival demo waitlist. No payment or commitment is required.</p>
            <div className="mx-auto mt-10 flex flex-col gap-3 print:hidden sm:flex-row">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90"><LocalizedLink to="/contact?intent=trade#enquiry-form">Request Trade Pricing</LocalizedLink></Button>
              <Button asChild size="lg" variant="outline" className="border-background/40 bg-transparent text-background hover:bg-background/10"><LocalizedLink to="/contact?intent=consultation#enquiry-form">Book Consultation</LocalizedLink></Button>
            </div>
            <div className="mx-auto mt-12 space-y-1 text-sm text-background/80">
              <p>www.aureum7trading.com/en/contact</p>
              <p>info@aureum7trading.com · +34 688 495 309</p>
              <p>Marbella, Spain</p>
            </div>
          </div>
          <Folio n={5} dark />
        </Page>
      </main>
    </div>
  );
}
